// useFetch.ts
import { useState, useEffect } from 'react';
import apiRequest from '../services/api';

export default function useFetch<T>(
    url: string,
    method: string = 'GET',
    body?: any,
    dependencies: any[] = []
) {
    const [data, setData] = useState<T | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        // Cancel ongoing requests when the dependencies change.
        // This prevents outdated API calls from overriding new ones.
        const controller = new AbortController();
        const signal = controller.signal;

        if (!url){
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            setLoading(true);
            setError(null);

            try {
                const response = await apiRequest<T>(url, method, body, {}, { signal });
                if (response.error) {
                    const errorMsg = response.error ?? 'Something went wrong';
                    console.log(
                        response.detail !== undefined ?
                        `[${errorMsg}]: ${response.detail}`:
                        errorMsg);
                    setError(
                        response.detail !== undefined ?
                        `[${errorMsg}]: ${response.detail}`:
                        errorMsg
                    );
                } else {
                    setData(response.data ?? null);
                }
            } catch (err: any) {
                if (!signal.aborted) {
                    setError(err.message ?? 'Something went wrong');
                }
            } finally {
                if (!signal.aborted) {
                    setLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            controller.abort();
        };
    }, dependencies);

    return { data, loading, error, setData };
};
